import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2500;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", attempts: 0, subscription: null, message: "" });
  const stopped = useRef(false);

  useEffect(() => {
    const poll = async (attempt) => {
      if (stopped.current) return;
      if (!sessionId) {
        setState({ status: "error", attempts: 0, subscription: null, message: "Missing session_id in URL" });
        return;
      }
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid" || data.applied) {
          await refresh();
          setState({ status: "success", attempts: attempt, subscription: data.subscription, message: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", attempts: attempt, subscription: null, message: "Checkout session expired." });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ status: "timeout", attempts: attempt, subscription: data.subscription, message: "Still processing — check your email for confirmation." });
          return;
        }
        setState((s) => ({ ...s, status: "polling", attempts: attempt }));
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", attempts: attempt, subscription: null, message: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll(1);
    return () => { stopped.current = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="billing-success-page">
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-20">
        {state.status === "polling" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="billing-polling">
            <Loader2 className="w-10 h-10 text-slate-400 mx-auto mb-5 animate-spin" />
            <h1 className="font-outfit text-3xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-slate-600 text-sm">Attempt {state.attempts} of {MAX_ATTEMPTS}. This usually takes a few seconds.</p>
          </div>
        )}
        {state.status === "success" && (
          <div className="bg-white border border-emerald-200 rounded-2xl p-10 text-center" data-testid="billing-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="font-outfit text-4xl font-bold text-slate-900">You&apos;re in.</h1>
            <p className="mt-3 text-slate-600">
              Welcome to Coast To Coast Permitting {state.subscription?.tier?.toUpperCase() || "Pro"}.
              Your subscription is active until <strong>{state.subscription?.access_until?.slice(0, 10) || "—"}</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="success-dashboard-cta">
                <Link to="/hot-markets">Open dashboard<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-11" data-testid="success-map-cta">
                <Link to="/map">Browse the map</Link>
              </Button>
            </div>
          </div>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <div className="bg-white border border-red-200 rounded-2xl p-10 text-center" data-testid="billing-error">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-9 h-9 text-red-600" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900">Payment not completed</h1>
            <p className="mt-3 text-slate-600 text-sm">{state.message}</p>
            <Button onClick={() => navigate("/pricing")} size="lg" className="mt-8 bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="error-retry-cta">
              Back to pricing
            </Button>
          </div>
        )}
        {state.status === "timeout" && (
          <div className="bg-white border border-amber-200 rounded-2xl p-10 text-center" data-testid="billing-timeout">
            <Loader2 className="w-10 h-10 text-amber-500 mx-auto mb-5" />
            <h1 className="font-outfit text-3xl font-bold text-slate-900">Still processing</h1>
            <p className="mt-3 text-slate-600 text-sm">{state.message}</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button onClick={() => navigate("/hot-markets")} variant="outline" className="h-11" data-testid="timeout-dashboard-cta">Open dashboard anyway</Button>
              <Button onClick={() => window.location.reload()} className="bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="timeout-retry-cta">Refresh</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
